import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Paper, Tooltip } from "@material-ui/core";
import { TabContext } from "@material-ui/lab";
import HomeIcon from "@material-ui/icons/Home";
import {
    Details,
    Validation,
    CustomerSummary,
    Annotations,
    PrintValidation,
    History,
    InfoPanelContainer,
    InfoTabPanel,
    InfoPanelContent,
    InfoAppBar,
    WelcomeTab,
    NarrowedTab,
} from "pages/MaterialSpecs/components/InfoPanel";
import Assets from "pages/MaterialSpecs/components/InfoPanel/Assets/Assets";
import { getMaterialDetailsById } from "store/features/materialSpecs/specs";
import { useMaterialSpecs, useMaterialDetails, useTabHeaders, TabHeader } from "pages/MaterialSpecs/hooks";
import { AdTabList } from "pages/MaterialSpecs/MaterialSpecs.styled";
import { ThirdPartyTagForm } from "../CreativesSection";
import { Versions } from "pages/MaterialSpecs/components/InfoPanel/Versions/Versions";

export const InfoPanel = () => {
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const {
        selectedMaterialSpecId,
        selectedMaterialId,
        setTab,
        isPrinted,
        tab,
        isOptionalSelected,
    } = useMaterialSpecs();
    const materialDetails = useSelector(getMaterialDetailsById(selectedMaterialSpecId, selectedMaterialId));
    const { isMaterialEmpty } = useMaterialDetails(materialDetails);
    const tabHeaders = useTabHeaders(materialDetails, isOptionalSelected, isMaterialEmpty, isPrinted);
    const stringifiedTabsParams = JSON.stringify(tabHeaders);

    useEffect(() => {
        const parsedTabs: TabHeader[] = JSON.parse(stringifiedTabsParams);
        const currentTab = parsedTabs.find(({ value }) => value === tab);

        // don't do anything, leave currently selected tab
        if (currentTab?.display && !currentTab?.disabled) {
            return;
        }

        // in other case find first available and select it
        const firstAvailableTab = parsedTabs.find(({ display, disabled }) => display && !disabled);

        if (firstAvailableTab) {
            setTab(firstAvailableTab.value);
        }
    }, [tab, setTab, selectedMaterialId, stringifiedTabsParams]);

    if ((!selectedMaterialSpecId || !selectedMaterialId) && !isOptionalSelected) {
        return null;
    }

    return (
        // TODO: https://github.com/mui-org/material-ui/pull/23686 Type issues, props inherited from "component" props are handled correctly
        <InfoPanelContainer component={Paper} overflow="hidden">
            <TabContext value={tab}>
                <InfoAppBar position="sticky">
                    <AdTabList onChange={(e, tab) => setTab(tab)} scrollButtons="auto" variant="scrollable">
                        {tabHeaders.map(({ tooltip, value, label, disabled, display }) => {
                            return (
                                <NarrowedTab
                                    key={value}
                                    value={value}
                                    style={{ display: display ? "inline-flex" : "none" }}
                                    label={
                                        <Tooltip title={tooltip}>
                                            {value === "WELCOME_TAB" ? <HomeIcon /> : <span>{label}</span>}
                                        </Tooltip>
                                    }
                                    disableRipple
                                    disabled={display ? disabled : display}
                                />
                            );
                        })}
                    </AdTabList>
                </InfoAppBar>

                <InfoPanelContent>
                    <InfoTabPanel value="WELCOME_TAB">
                        <WelcomeTab />
                    </InfoTabPanel>
                    <InfoTabPanel value="CUSTOMER_SUMMARY">
                        <CustomerSummary />
                    </InfoTabPanel>
                    {selectedMaterialSpecId && selectedMaterialId && (
                        <InfoTabPanel value="DETAILS" stickyFooter>
                            <Details materialSpecId={selectedMaterialSpecId} materialId={selectedMaterialId} />
                        </InfoTabPanel>
                    )}
                    {/*TODO: temporary inline style, remove when print validation will be handled properly on server*/}
                    <InfoTabPanel value="VALIDATION" style={{ height: isPrinted ? "100%" : "initial" }}>
                        {isPrinted ? <PrintValidation /> : <Validation />}
                    </InfoTabPanel>
                    <InfoTabPanel value="THIRD_PARTY_TAG">
                        <ThirdPartyTagForm isMaterialEmpty={isMaterialEmpty} disableAccordion />
                    </InfoTabPanel>
                    <InfoTabPanel value="ANNOTATIONS" stickyHeader stickyFooter>
                        <Annotations />
                    </InfoTabPanel>
                    <InfoTabPanel value="HISTORY" stickyHeader>
                        <History />
                    </InfoTabPanel>
                    <InfoTabPanel value="ASSETS" stickyFooter innerRef={tabContainerRef}>
                        <Assets ref={tabContainerRef} />
                    </InfoTabPanel>
                    <InfoTabPanel value="VERSIONS">
                        <Versions />
                    </InfoTabPanel>
                </InfoPanelContent>
            </TabContext>
        </InfoPanelContainer>
    );
};
